"use client";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import { Checkbox, Col, DatePicker, Form, Row, Select, Tooltip } from "antd";

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const metricBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 16,
};

const metricLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  marginBottom: 4,
};

const metricValueStyle = {
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.3,
};

const acceptanceMethodOptions = [
  { value: "in_person_branch", label: "In-person signature -- branch" },
  { value: "digital", label: "Digital acceptance -- OTP" },
  { value: "postal", label: "Postal / courier" },
];

const OfferAcceptance = (props) => {
  const { onFinish, form, initialValues, loanData } = props;

  const approvedAmount = loanData?.approvedAmount ?? "N5,000,000";
  const interestRate = loanData?.interestRate ?? "22.00% p.a.";
  const monthlyEmi = loanData?.monthlyEmi ?? "N273,104";
  const tenor = loanData?.tenor ?? "24 months";
  const processingFee = loanData?.processingFee ?? "N75,000";
  const offerValidUntil = loanData?.offerValidUntil ?? "12 Jun 2026";
  const customerName =
    loanData?.customerName ?? "Emeka Chukwunonso Nwosu";
  const loanRef = loanData?.loanRef ?? "LN-APP-2026-0847";
  const generatedDate = loanData?.generatedDate ?? "29 May 2026";

  return (
    <div>
      {/* Offer Letter Card */}
      <div style={{ marginBottom: 24 }}>
        {/* Dark Header */}
        <div
          style={{
            background: "#15CCAE",
            borderRadius: "12px 12px 0 0",
            padding: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#F59E0B",
              }}
            />
            <span
              style={{
                color: "white",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Loan Offer Letter -- {loanRef}
            </span>
          </div>
          <span style={{ color: "#98A2B3", fontSize: 13 }}>
            Generated: {generatedDate}
          </span>
        </div>

        {/* Body */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            padding: 24,
          }}
        >
          <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>
            Dear {customerName},
          </p>
          <p
            style={{
              fontSize: 14,
              color: "#475467",
              lineHeight: 1.6,
              marginBottom: 24,
            }}
          >
            We are pleased to offer you a loan facility under the following
            terms and conditions, subject to your formal acceptance and
            satisfaction of all pre-disbursement requirements.
          </p>

          {/* Metric Boxes */}
          <Row style={{ marginBottom: 16 }}>
            <Col span={8} style={{ padding: "0px 8px", marginBottom: 16 }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>APPROVED AMOUNT</div>
                <div style={metricValueStyle}>{approvedAmount}</div>
              </div>
            </Col>
            <Col span={8} style={{ padding: "0px 8px", marginBottom: 16 }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>INTEREST RATE</div>
                <div style={{ ...metricValueStyle, color: "#15CCAE" }}>
                  {interestRate}
                </div>
              </div>
            </Col>
            <Col span={8} style={{ padding: "0px 8px", marginBottom: 16 }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>MONTHLY EMI</div>
                <div style={metricValueStyle}>{monthlyEmi}</div>
              </div>
            </Col>
            <Col span={8} style={{ padding: "0px 8px" }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>TENOR</div>
                <div style={metricValueStyle}>{tenor}</div>
              </div>
            </Col>
            <Col span={8} style={{ padding: "0px 8px" }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>PROCESSING FEE</div>
                <div style={metricValueStyle}>{processingFee}</div>
              </div>
            </Col>
            <Col span={8} style={{ padding: "0px 8px" }}>
              <div style={metricBoxStyle}>
                <div style={metricLabelStyle}>OFFER VALID UNTIL</div>
                <div style={{ ...metricValueStyle, color: "#DC2626" }}>
                  {offerValidUntil}
                </div>
              </div>
            </Col>
          </Row>

          <p
            style={{
              fontSize: 13,
              color: "#475467",
              lineHeight: 1.6,
              background: "#F9FAFB",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <span style={{ fontWeight: 600 }}>
              Conditions precedent to disbursement:
            </span>{" "}
            (1) Submission of 3-month bank statement &middot; (2) Execution
            of loan agreement and debenture &middot; (3) Registration of
            vehicle logbook as collateral &middot; (4) Salary domiciliation
            mandate signed by employer
          </p>
        </div>
      </div>

      {/* Customer Acceptance */}
      <div style={sectionDividerStyle}>
        <div style={sectionHeaderStyle}>CUSTOMER ACCEPTANCE</div>

        <Form
          className="custom-form"
          layout="vertical"
          onFinish={onFinish}
          form={form}
          initialValues={initialValues}
          style={{ width: "100%" }}
        >
          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Customer Acceptance Method
                    <Tooltip title="Select how the customer will accept the offer">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="acceptanceMethod"
                rules={[
                  {
                    required: true,
                    message: "Please select acceptance method",
                  },
                ]}
              >
                <Select
                  placeholder="Select acceptance method"
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" width={20} />}
                  options={acceptanceMethodOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Acceptance Date
                    <Tooltip title="Select the date of customer acceptance">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="acceptanceDate"
                rules={[
                  {
                    required: true,
                    message: "Acceptance date is required",
                  },
                ]}
              >
                <DatePicker
                  className="bond-date-picker"
                  style={{
                    width: "100%",
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 11px",
                    fontSize: 14,
                  }}
                  suffixIcon={<CalendarIcon color="#667085" width={20} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item
                name="customerConfirmation"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Please confirm the customer has accepted the offer"
                            )
                          ),
                  },
                ]}
              >
                <Checkbox>
                  I confirm the customer has read, understood and accepted
                  all terms of this loan offer letter. The signed document
                  has been filed.
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default OfferAcceptance;
